/**
 * Brands.jsx
 * Brand profiles — the source of truth for every AI prompt the app builds and
 * every account it publishes to. That's why it asks for tone, audience and
 * banned claims rather than just a name.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBrands } from '../hooks/useBrands.jsx';
import { api } from '../services/api.js';
import BrandForm from '../components/BrandForm.jsx';
import ConnectAccounts from '../components/ConnectAccounts.jsx';
import { Plus, Tag, Warning, Check } from '../components/Icons.jsx';

export default function Brands() {
  const { brands, loading, refresh, selectBrand } = useBrands();
  const [editing, setEditing] = useState(null); // brand object, 'new', or null
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();

  // Meta sends the browser back here after sign-in, with the outcome in the URL.
  const connected = params.get('connected');
  const connectError = params.get('connect_error');
  // What the sign-in actually did to the existing accounts. "Facebook
  // connected" on its own was the misleading part: it was true, and every
  // account was still broken behind it.
  const connectNote = params.get('note');

  useEffect(() => {
    if (connected || connectError) refresh();
  }, [connected, connectError, refresh]);

  async function save(data) {
    setBusy(true);
    setError(null);
    try {
      if (editing === 'new') {
        const { brand } = await api.brands.create(data);
        await refresh();
        selectBrand(brand.id);
      } else {
        await api.brands.update(editing.id, data);
        await refresh();
      }
      setEditing(null);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function archive(brand) {
    if (!confirm(`Archive "${brand.name}"? Its published posts stay in the history.`)) return;
    setBusy(true);
    try {
      await api.brands.archive(brand.id);
      await refresh();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <>
        <div className="page-head">
          <div>
            <div className="page-head__eyebrow">Brands</div>
            <h1>{editing === 'new' ? 'New brand' : `Edit ${editing.name}`}</h1>
            <p>
              Everything here is fed into every caption and image prompt for this brand — so you
              never have to explain the brand to the AI again.
            </p>
          </div>
        </div>

        {error && (
          <div className="notice notice--error">
            <Warning />
            <span>{error.message}</span>
          </div>
        )}

        <BrandForm
          brand={editing === 'new' ? null : editing}
          busy={busy}
          onSave={save}
          onCancel={() => {
            setEditing(null);
            setError(null);
          }}
        />
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Brands</div>
          <h1>Your brands</h1>
          <p>Each brand keeps its own voice, audience, colours and connected accounts.</p>
        </div>
        <div className="page-head__actions">
          <button className="btn btn--primary" onClick={() => setEditing('new')}>
            <Plus /> New brand
          </button>
        </div>
      </div>

      {connectError && (
        <div className="notice notice--error">
          <Warning />
          <span>Facebook sign-in failed: {connectError}</span>
        </div>
      )}

      {connected && !connectError && (
        <div className="notice notice--info">
          <Check />
          <span>
            {connectNote ?? 'Facebook connected.'}{' '}
            {/* Only worth saying when there is nothing already attached; after a
                reconnect the accounts are back and there is nothing to choose. */}
            {!connectNote && 'Choose which Page belongs to which brand below.'}
          </span>
        </div>
      )}

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {loading && <p className="mono">Loading…</p>}

      {!loading && brands.length === 0 && (
        <div className="card">
          <div className="empty">
            <div className="empty__art">
              <Tag size={30} />
            </div>
            <h3>No brands yet</h3>
            <p>
              Start with Archevot — it’s the brand with both a Facebook Page
              <br />
              and a linked Instagram account, so it’s what Phase 2 will publish through.
            </p>
            <button className="btn btn--primary" onClick={() => setEditing('new')}>
              <Plus /> Add your first brand
            </button>
          </div>
        </div>
      )}

      {brands.map((brand) => (
        <section className="card" key={brand.id}>
          <div className="brand-card__title">
            <span className="tile">
              <Tag size={21} />
            </span>
            <h2>{brand.name}</h2>
            <div className="card__head-end">
              <button className="btn btn--sm" onClick={() => setEditing(brand)}>
                Edit
              </button>
              <button className="btn btn--sm btn--danger" onClick={() => archive(brand)}>
                Archive
              </button>
            </div>
          </div>

          {brand.product_summary && <p className="brand-card__summary">{brand.product_summary}</p>}

          <div className="rows">
            <Detail label="Tone of voice" value={brand.tone} />
            <Detail label="Audience" value={brand.audience} />
            <Detail label="Never claim" value={brand.banned_claims} />
            <Detail label="Default CTA" value={brand.default_cta} />
            <Detail label="Image ratio" value={brand.default_aspect} />

            <div className="row">
              <div>
                <div className="row__label">Brand colours</div>
              </div>
              <div className="row__end">
                {brand.colors.length ? (
                  <span className="swatches">
                    {brand.colors.map((c) => (
                      <span key={c} className="swatch" style={{ background: c }} title={c} />
                    ))}
                  </span>
                ) : (
                  'Not set'
                )}
              </div>
            </div>

            <ConnectAccounts brand={brand} accounts={brand.accounts} onChanged={refresh} />
          </div>
        </section>
      ))}
    </>
  );
}

function Detail({ label, value }) {
  return (
    <div className="row">
      <div style={{ minWidth: 0 }}>
        <div className="row__label">{label}</div>
      </div>
      <div className="row__end" style={{ maxWidth: '62%' }}>
        {value || 'Not set'}
      </div>
    </div>
  );
}
